/**
 * Cita Events - Checkout Core State Module
 * =========================================
 * Unified State Management for all Checkout Implementations
 *
 * This module provides:
 * - State factory functions
 * - State update helpers
 * - Type definitions for state objects
 *
 * @version 1.0.0
 * @module checkout-core/state
 */

/**
 * @typedef {Object} GroupPerson
 * @property {string} first_name - First name
 * @property {string} last_name - Last name
 * @property {string} email - Email address
 * @property {string} [birth_date] - Birth date (YYYY-MM-DD)
 * @property {boolean} [age_confirmed] - Age confirmation checkbox
 * @property {Object} [custom_fields] - Organizer-defined custom fields
 */

/**
 * @typedef {Object} PlusOnePerson
 * @property {string} first_name - First name
 * @property {string} last_name - Last name
 * @property {string} email - Email address
 * @property {string} [birth_date] - Birth date (YYYY-MM-DD)
 * @property {boolean} [age_confirmed] - Age confirmation checkbox
 */

/**
 * @typedef {Object} TicketData
 * @property {string} ticketId - Ticket type ID
 * @property {string} ticketName - Display name
 * @property {number} price - Base price
 * @property {string} currency - Currency code (e.g., 'EUR')
 * @property {boolean} isPaid - Whether ticket requires payment
 * @property {boolean} isFree - Whether ticket is free
 * @property {boolean} requiresApproval - Whether ticket requires organizer approval
 * @property {number} minAge - Minimum age requirement
 * @property {string} ageVerificationMode - 'birth_date' or 'checkbox'
 * @property {number} minPerOrder - Minimum tickets per order
 * @property {number} maxPerOrder - Maximum tickets per order
 * @property {boolean} allowGroup - Whether group booking is allowed
 * @property {number} groupMin - Minimum for group discount
 * @property {number} groupMax - Maximum group size
 * @property {string} groupDiscountType - 'Prozent' or 'Betrag'
 * @property {number} groupDiscountValue - Discount value
 * @property {boolean} allowPlusOne - Whether plus-ones are allowed
 * @property {number} plusOneMax - Maximum plus-ones per ticket
 * @property {boolean} askMembership - Whether membership number is requested
 * @property {boolean} membershipRequired - Whether membership number is required
 * @property {string} membershipLabel - Label for membership number field
 * @property {string} membershipHelp - Help text for membership number field
 * @property {Array} tiers - Tier pricing array
 * @property {number|null} remainingCount - Remaining tickets available
 * @property {number|null} maxQuantity - Maximum ticket capacity
 * @property {boolean} soldOut - Whether ticket is sold out
 * @property {Array} customFieldDefs - Custom registration field definitions
 */

/**
 * @typedef {Object} CheckoutState
 * @property {string|null} selectedTicket - Selected ticket ID
 * @property {TicketData} ticketData - Current ticket configuration
 * @property {number} quantity - Number of paid tickets (1 = main person only)
 * @property {GroupPerson[]} groupPersons - Group members (starting from person 2)
 * @property {PlusOnePerson[]} plusOnes - Plus-one persons (free)
 * @property {string|null} promoCode - Applied promo code
 * @property {number} promoDiscount - Promo discount amount
 * @property {number} groupDiscount - Calculated group discount
 * @property {string|null} selectedTier - Selected tier name
 * @property {boolean} isSubmitting - Form submission in progress
 * @property {boolean} skipAccountCheck - Skip email account check (after login modal)
 */

/**
 * Create a fresh checkout state object.
 *
 * @param {Partial<CheckoutState>} [initial={}] - Initial state overrides
 * @returns {CheckoutState} New checkout state
 */
export function createCheckoutState(initial = {}) {
  return {
    selectedTicket: null,
    ticketData: {},
    quantity: 1,
    groupPersons: [],
    plusOnes: [],
    promoCode: null,
    promoDiscount: 0,
    groupDiscount: 0,
    membershipNumber: '',
    selectedTier: null,
    isSubmitting: false,
    skipAccountCheck: false,
    ...initial,
  };
}

/**
 * Create a fresh ticket data object.
 *
 * @param {Partial<TicketData>} [initial={}] - Initial data overrides
 * @returns {TicketData} New ticket data
 */
export function createTicketData(initial = {}) {
  return {
    ticketId: '',
    ticketName: '',
    price: 0,
    currency: 'EUR',
    isPaid: false,
    isFree: false,
    requiresApproval: false,
    // WELLE 1 / P1-1: wallet (Apple/Google Pay) master switch, mirrored from
    // `data-wallet-enabled`. Default false = fail-closed (no wallet UI).
    walletEnabled: false,
    minAge: 0,
    ageVerificationMode: 'birth_date',
    minPerOrder: 1,
    maxPerOrder: 10,
    allowGroup: false,
    groupMin: 2,
    groupMax: 10,
    groupDiscountType: '',
    groupDiscountValue: 0,
    maxCombinedDiscountPercent: 100,
    allowPlusOne: false,
    plusOneMax: 1,
    askMembership: false,
    membershipRequired: false,
    membershipLabel: 'Zusatzangabe (optional)',
    membershipHelp: '',
    membershipDiscountType: '',
    membershipDiscountValue: 0,
    tiers: [],
    remainingCount: null,
    maxQuantity: null,
    soldOut: false,
    customFieldDefs: [],
    ...initial,
  };
}

/**
 * Extract ticket data from a DOM element's data attributes.
 *
 * @param {HTMLElement} element - Element with data-* attributes
 * @returns {TicketData} Extracted ticket data
 */
export function extractTicketDataFromElement(element) {
  if (!element || !element.dataset) {
    return createTicketData();
  }

  const ds = element.dataset;

  return createTicketData({
    ticketId: ds.ticket || ds.ticketId || '',
    ticketName: ds.ticketName || ds.ticketLabel || '',
    price: parseFloat(ds.ticketPrice || ds.price) || 0,
    currency: ds.currency || 'EUR',
    isPaid: ds.ispaid === '1' || ds.ispaid === 'true' || ds.isPaid === '1' || ds.isPaid === 'true',
    isFree: ds.isfree === '1' || ds.isfree === 'true' || ds.isFree === '1' || ds.isFree === 'true',
    requiresApproval: ds.requiresApproval === '1' || ds.requiresApproval === 'true',
    // WELLE 1 / P1-1: wallet UI master switch. Absent attribute → undefined →
    // false (fail-closed: no Apple/Google Pay button).
    walletEnabled: ds.walletEnabled === '1' || ds.walletEnabled === 'true',
    minAge: parseInt(ds.minage || ds.minAge || '0', 10),
    ageVerificationMode: ds.ageMode || ds.agemode || ds.ageverificationmode || 'birth_date',
    minPerOrder: parseInt(ds.minPerOrder || ds.minperorder || '1', 10),
    maxPerOrder: parseInt(ds.maxPerOrder || ds.maxperorder || '10', 10),
    allowGroup: ds.allowGroup === '1' || ds.allowGroup === 'true',
    groupMin: parseInt(ds.groupMin || '2', 10),
    groupMax: parseInt(ds.groupMax || '10', 10),
    groupDiscountType: ds.groupDiscountType || '',
    groupDiscountValue: parseFloat(ds.groupDiscountValue) || 0,
    maxCombinedDiscountPercent: parseFloat(ds.maxCombinedDiscountPercent || ds.maxcombineddiscountpercent || '100') || 100,
    allowPlusOne: ds.allowPlusOne === '1' || ds.allowPlusOne === 'true',
    plusOneMax: Math.min(parseInt(ds.plusOneMax || ds.maxPlusOne || '1', 10) || 1, 1),
    askMembership: ds.askMembership === '1' || ds.askMembership === 'true',
    membershipRequired: ds.membershipRequired === '1' || ds.membershipRequired === 'true',
    membershipLabel: ds.membershipLabel || 'Zusatzangabe (optional)',
    membershipHelp: ds.membershipHelp || '',
    // BUG-1 FIX: Membership discount config for FE pricing parity
    membershipDiscountType: ds.membershipDiscountType || '',
    membershipDiscountValue: parseFloat(ds.membershipDiscountValue) || 0,
    tiers: parseTiers(ds.tiers),
    customFieldDefs: parseCustomFieldDefs(ds.customFieldDefs),
    // Capacity data for "Fast weg!" display
    remainingCount: ds.remainingCount !== undefined ? parseInt(ds.remainingCount, 10) : null,
    maxQuantity: ds.maxQuantity !== undefined ? parseInt(ds.maxQuantity, 10) : null,
    soldOut: ds.soldOut === '1' || ds.soldOut === 'true',
  });
}

/**
 * Parse custom field definitions from a JSON data attribute.
 *
 * @param {string|undefined} raw - JSON string or undefined
 * @returns {Array} Parsed custom field definitions
 */
function parseCustomFieldDefs(raw) {
  if (!raw) return [];
  try {
    const defs = JSON.parse(raw);
    return Array.isArray(defs) ? defs : [];
  } catch {
    return [];
  }
}

/**
 * Parse tiers from a JSON string or data attribute.
 *
 * @param {string|undefined} tiersData - JSON string or undefined
 * @returns {Array} Parsed tiers array
 * @private
 */
function parseTiers(tiersData) {
  if (!tiersData) return [];

  try {
    const parsed = JSON.parse(tiersData);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

/**
 * Create an empty group person object.
 *
 * @returns {GroupPerson} Empty group person
 */
export function createGroupPerson() {
  return {
    first_name: '',
    last_name: '',
    email: '',
    birth_date: '',
    age_confirmed: false,
  };
}

/**
 * Create an empty plus-one person object.
 *
 * @returns {PlusOnePerson} Empty plus-one person
 */
export function createPlusOnePerson() {
  return {
    first_name: '',
    last_name: '',
    email: '',
    birth_date: '',
    age_confirmed: false,
  };
}

/**
 * Add a group person to state.
 *
 * @param {CheckoutState} state - Current state
 * @param {Partial<GroupPerson>} [person={}] - Person data
 * @returns {CheckoutState} Updated state (mutates original)
 */
export function addGroupPerson(state, person = {}) {
  const maxPersons = (state.ticketData?.groupMax || 10) - 1; // -1 because main person is not in groupPersons
  if (state.groupPersons.length >= maxPersons) {
    return state;
  }

  state.groupPersons.push({
    ...createGroupPerson(),
    ...person,
  });
  state.quantity = state.groupPersons.length + 1;

  return state;
}

/**
 * Remove a group person from state.
 *
 * @param {CheckoutState} state - Current state
 * @param {number} index - Index to remove
 * @returns {CheckoutState} Updated state (mutates original)
 */
export function removeGroupPerson(state, index) {
  if (index >= 0 && index < state.groupPersons.length) {
    state.groupPersons.splice(index, 1);
    state.quantity = state.groupPersons.length + 1;
  }
  return state;
}

/**
 * Update a group person at a specific index.
 *
 * @param {CheckoutState} state - Current state
 * @param {number} index - Index to update
 * @param {Partial<GroupPerson>} data - Data to merge
 * @returns {CheckoutState} Updated state (mutates original)
 */
export function updateGroupPerson(state, index, data) {
  if (index >= 0 && index < state.groupPersons.length) {
    state.groupPersons[index] = {
      ...state.groupPersons[index],
      ...data,
    };
  }
  return state;
}

/**
 * Add a plus-one person to state.
 *
 * @param {CheckoutState} state - Current state
 * @param {Partial<PlusOnePerson>} [person={}] - Person data
 * @returns {CheckoutState} Updated state (mutates original)
 */
export function addPlusOne(state, person = {}) {
  const maxPlusOnes = state.ticketData?.plusOneMax || 1;
  if (state.plusOnes.length >= maxPlusOnes) {
    return state;
  }

  state.plusOnes.push({
    ...createPlusOnePerson(),
    ...person,
  });

  return state;
}

/**
 * Remove a plus-one person from state.
 *
 * @param {CheckoutState} state - Current state
 * @param {number} index - Index to remove
 * @returns {CheckoutState} Updated state (mutates original)
 */
export function removePlusOne(state, index) {
  if (index >= 0 && index < state.plusOnes.length) {
    state.plusOnes.splice(index, 1);
  }
  return state;
}

/**
 * Update a plus-one person at a specific index.
 *
 * @param {CheckoutState} state - Current state
 * @param {number} index - Index to update
 * @param {Partial<PlusOnePerson>} data - Data to merge
 * @returns {CheckoutState} Updated state (mutates original)
 */
export function updatePlusOne(state, index, data) {
  if (index >= 0 && index < state.plusOnes.length) {
    state.plusOnes[index] = {
      ...state.plusOnes[index],
      ...data,
    };
  }
  return state;
}

/**
 * Set quantity and adjust group persons array accordingly.
 *
 * @param {CheckoutState} state - Current state
 * @param {number} newQuantity - New quantity
 * @returns {CheckoutState} Updated state (mutates original)
 */
export function setQuantity(state, newQuantity) {
  const ticket = state.ticketData || {};
  const allowGroup = ticket.allowGroup === true || ticket.allowGroup === '1' || ticket.allowGroup === 'true';

  const minPerOrder = parseInt(ticket.minPerOrder || '1', 10) || 1;
  const maxPerOrder = parseInt(ticket.maxPerOrder || '10', 10) || 10;
  const groupMin = parseInt(ticket.groupMin || '1', 10) || 1;
  const groupMax = parseInt(ticket.groupMax || '10', 10) || 10;

  if (!allowGroup) {
    state.quantity = 1;
    state.groupPersons = [];
    return state;
  }

  // groupMin controls discount threshold only, not minimum order quantity.
  // This allows single-ticket purchases on group-enabled tickets.
  let minQty = Math.max(minPerOrder, 1);
  // BUG-3 FIX: For group bookings, group_max_size is the effective max (not max_per_order).
  // max_per_order is an anti-scalper measure for individual purchases.
  // Backend (stripe_checkout.py:1507-1509) uses only group_max_size for groups.
  // Hard cap at 10 matches GROUP_MAX_HARD_CAP in group_members.py and stripe_checkout.py.
  let maxQty = Math.min(groupMax, 10);
  if (maxQty < minQty) {
    maxQty = minQty;
  }

  const quantity = Math.max(minQty, Math.min(maxQty, newQuantity || minQty));
  state.quantity = quantity;

  // Adjust groupPersons array to match quantity
  const groupSize = quantity - 1; // -1 for main person

  // Add missing persons
  while (state.groupPersons.length < groupSize) {
    state.groupPersons.push(createGroupPerson());
  }

  // Remove excess persons
  while (state.groupPersons.length > groupSize) {
    state.groupPersons.pop();
  }

  return state;
}

/**
 * Apply a promo code discount to state.
 *
 * @param {CheckoutState} state - Current state
 * @param {string} code - Promo code
 * @param {number} discount - Discount amount
 * @returns {CheckoutState} Updated state (mutates original)
 */
export function applyPromoCode(state, code, discount) {
  state.promoCode = code;
  state.promoDiscount = discount;
  return state;
}

/**
 * Remove applied promo code from state.
 *
 * @param {CheckoutState} state - Current state
 * @returns {CheckoutState} Updated state (mutates original)
 */
export function removePromoCode(state) {
  state.promoCode = null;
  state.promoDiscount = 0;
  return state;
}

/**
 * Reset checkout state to initial values.
 *
 * @param {CheckoutState} state - Current state
 * @returns {CheckoutState} Reset state (mutates original)
 */
export function resetCheckoutState(state) {
  state.selectedTicket = null;
  state.ticketData = {};
  state.quantity = 1;
  state.groupPersons = [];
  state.plusOnes = [];
  state.promoCode = null;
  state.promoDiscount = 0;
  state.groupDiscount = 0;
  state.selectedTier = null;
  state.isSubmitting = false;
  state.skipAccountCheck = false;
  return state;
}

/**
 * Sync group persons data from form inputs.
 *
 * @param {HTMLFormElement} form - The form element
 * @param {CheckoutState} state - Current state
 * @returns {CheckoutState} Updated state (mutates original)
 */
export function syncGroupPersonsFromForm(form, state) {
  if (!form) return state;

  state.groupPersons = [];

  // Find all group person fields by index
  let index = 0;
  while (true) {
    const firstName = form.querySelector(`[name="group_first_name_${index}"]`);
    if (!firstName) break;

    const lastName = form.querySelector(`[name="group_last_name_${index}"]`);
    const email = form.querySelector(`[name="group_email_${index}"]`);
    const birthDate = form.querySelector(`[name="group_birth_date_${index}"]`);
    const ageConfirmed = form.querySelector(`[name="group_age_confirmed_${index}"]`);

    const membershipInput = form.querySelector(`[name="group_membership_number_${index}"]`);
    state.groupPersons.push({
      first_name: firstName?.value || '',
      last_name: lastName?.value || '',
      email: email?.value || '',
      birth_date: birthDate?.value || '',
      age_confirmed: ageConfirmed?.checked || false,
      membership_number: membershipInput ? (membershipInput.value || '').trim() : '',
      custom_fields: collectPersonCustomFields(form, 'group_', index),
    });

    index++;
  }

  state.quantity = state.groupPersons.length + 1;
  return state;
}

/**
 * Sync plus-ones data from form inputs.
 *
 * @param {HTMLFormElement} form - The form element
 * @param {CheckoutState} state - Current state
 * @returns {CheckoutState} Updated state (mutates original)
 */
export function syncPlusOnesFromForm(form, state) {
  if (!form) return state;

  state.plusOnes = [];

  // Find all plus-one fields by index
  let index = 0;
  while (true) {
    const firstName = form.querySelector(`[name="plus_one_first_name_${index}"]`);
    if (!firstName) break;

    const lastName = form.querySelector(`[name="plus_one_last_name_${index}"]`);
    const email = form.querySelector(`[name="plus_one_email_${index}"]`);
    const birthDate = form.querySelector(`[name="plus_one_birth_date_${index}"]`);
    const ageConfirmed = form.querySelector(`[name="plus_one_age_confirmed_${index}"]`);

    const membershipInput = form.querySelector(`[name="plus_one_membership_number_${index}"]`);
    state.plusOnes.push({
      first_name: firstName?.value || '',
      last_name: lastName?.value || '',
      email: email?.value || '',
      birth_date: birthDate?.value || '',
      age_confirmed: ageConfirmed?.checked || false,
      membership_number: membershipInput ? (membershipInput.value || '').trim() : '',
      custom_fields: collectPersonCustomFields(form, 'plus_one_', index),
    });

    index++;
  }

  return state;
}

/**
 * Collect custom field values for a specific person from form inputs.
 *
 * @param {HTMLFormElement} form - The form element
 * @param {string} prefix - Name prefix ('group_' or 'plus_one_')
 * @param {number} index - Person index
 * @returns {Object|null} Custom fields payload or null
 */
function collectPersonCustomFields(form, prefix, index) {
  const fields = form.querySelectorAll(`[name^="${prefix}cf_"][name$="_${index}"]`);
  if (!fields.length) return null;

  const payload = {};
  fields.forEach(el => {
    const key = el.dataset.personCf;
    if (!key) return;
    if (el.type === 'checkbox') {
      payload[key] = !!el.checked;
    } else {
      const val = (el.value || '').trim();
      if (val !== '') payload[key] = val;
    }
  });
  return Object.keys(payload).length ? payload : null;
}

export default {
  createCheckoutState,
  createTicketData,
  extractTicketDataFromElement,
  createGroupPerson,
  createPlusOnePerson,
  addGroupPerson,
  removeGroupPerson,
  updateGroupPerson,
  addPlusOne,
  removePlusOne,
  updatePlusOne,
  setQuantity,
  applyPromoCode,
  removePromoCode,
  resetCheckoutState,
  syncGroupPersonsFromForm,
  syncPlusOnesFromForm,
};
